/**
 * Conflict Resolution for Offline Edits
 * Handles scenarios where multiple users edit the same data offline
 */

export interface Conflict {
  id: string;
  entity: 'well' | 'entry' | 'message' | 'task' | 'note';
  entityId: string;
  field: string;
  localValue: any;
  serverValue: any;
  localTimestamp: number;
  serverTimestamp: number;
  resolution?: 'local' | 'server' | 'merge';
}

export interface ConflictResolutionStrategy {
  strategy: 'last-write-wins' | 'manual' | 'merge' | 'field-level';
  preferences?: {
    preferLocal?: boolean;
    preferServer?: boolean;
    autoMergeFields?: string[];
  };
}

class ConflictResolver {
  private conflicts: Conflict[] = [];
  private strategy: ConflictResolutionStrategy = {
    strategy: 'last-write-wins',
  };

  setStrategy(strategy: ConflictResolutionStrategy): void {
    this.strategy = strategy;
  }

  /**
   * Detect conflicts between local and server data
   */
  detectConflicts(
    entity: 'well' | 'entry' | 'message' | 'task' | 'note',
    entityId: string,
    localData: any,
    serverData: any
  ): Conflict[] {
    const conflicts: Conflict[] = [];

    // Compare timestamps
    const localTimestamp = localData.lastModified || localData.timestamp || 0;
    const serverTimestamp = serverData.lastModified || serverData.timestamp || 0;

    // If timestamps are very close (within 1 second), no conflict
    if (Math.abs(localTimestamp - serverTimestamp) < 1000) {
      return conflicts;
    }

    // Check each field for differences
    const allKeys = new Set([...Object.keys(localData), ...Object.keys(serverData)]);
    
    for (const key of allKeys) {
      // Skip metadata fields
      if (['id', 'lastSync', 'lastModified', 'createdAt'].includes(key)) {
        continue;
      }

      const localValue = localData[key];
      const serverValue = serverData[key];

      // Check if values are different
      if (JSON.stringify(localValue) !== JSON.stringify(serverValue)) {
        conflicts.push({
          id: `conflict-${entityId}-${key}-${Date.now()}`,
          entity,
          entityId,
          field: key,
          localValue,
          serverValue,
          localTimestamp,
          serverTimestamp,
        });
      }
    }

    return conflicts;
  }

  /**
   * Automatically resolve conflicts based on strategy
   */
  async autoResolve(conflicts: Conflict[]): Promise<{ resolved: any; remaining: Conflict[] }> {
    const remaining: Conflict[] = [];
    const resolved: Record<string, any> = {};

    for (const conflict of conflicts) {
      let resolution: 'local' | 'server' | 'merge' | null = null;

      switch (this.strategy.strategy) {
        case 'last-write-wins':
          // Use the most recent data
          resolution = conflict.localTimestamp > conflict.serverTimestamp ? 'local' : 'server';
          break;

        case 'manual':
          // All conflicts require manual resolution
          remaining.push(conflict);
          continue;

        case 'merge':
          // Attempt to merge both values
          resolution = 'merge';
          break;

        case 'field-level':
          // Check if this field is in auto-merge list
          if (this.strategy.preferences?.autoMergeFields?.includes(conflict.field)) {
            resolution = 'merge';
          } else {
            remaining.push(conflict);
            continue;
          }
          break;
      }

      // Apply resolution
      if (resolution) {
        resolved[conflict.field] = this.applyResolution(conflict, resolution);
      }
    }

    return { resolved, remaining };
  }

  private applyResolution(conflict: Conflict, resolution: 'local' | 'server' | 'merge'): any {
    switch (resolution) {
      case 'local':
        return conflict.localValue;
      
      case 'server':
        return conflict.serverValue;
      
      case 'merge':
        return this.mergeValues(conflict.localValue, conflict.serverValue, conflict.field);
      
      default:
        return conflict.serverValue; // Default to server
    }
  }

  private mergeValues(localValue: any, serverValue: any, field: string): any {
    // Handle different data types
    
    // Numbers: take average
    if (typeof localValue === 'number' && typeof serverValue === 'number') {
      return (localValue + serverValue) / 2;
    }

    // Arrays: combine and deduplicate
    if (Array.isArray(localValue) && Array.isArray(serverValue)) {
      return [...new Set([...localValue, ...serverValue])];
    }

    // Objects: merge properties
    if (typeof localValue === 'object' && typeof serverValue === 'object') {
      return { ...serverValue, ...localValue };
    }

    // Strings: for notes/comments, concatenate with separator
    if (typeof localValue === 'string' && typeof serverValue === 'string') {
      if (field.toLowerCase().includes('note') || field.toLowerCase().includes('comment')) {
        return `${serverValue}\n---\n${localValue}`;
      }
      // For other strings, prefer local
      return localValue;
    }

    // Boolean: prefer true (more permissive)
    if (typeof localValue === 'boolean' && typeof serverValue === 'boolean') {
      return localValue || serverValue;
    }

    // Default: prefer local
    return localValue;
  }

  /**
   * Store conflicts for manual resolution
   */
  addConflicts(conflicts: Conflict[]): void {
    this.conflicts.push(...conflicts);
  }

  /**
   * Get all unresolved conflicts
   */
  getConflicts(): Conflict[] {
    return this.conflicts;
  }

  /**
   * Get conflicts for a specific entity
   */
  getConflictsForEntity(entity: string, entityId: string): Conflict[] {
    return this.conflicts.filter(c => c.entity === entity && c.entityId === entityId);
  }

  /**
   * Resolve a specific conflict manually
   */
  resolveConflict(conflictId: string, resolution: 'local' | 'server' | 'merge'): any {
    const conflict = this.conflicts.find(c => c.id === conflictId);
    if (!conflict) {
      throw new Error('Conflict not found');
    }

    const resolvedValue = this.applyResolution(conflict, resolution);
    
    // Remove from conflicts list
    this.conflicts = this.conflicts.filter(c => c.id !== conflictId);
    
    return resolvedValue;
  }

  /**
   * Resolve all conflicts for an entity
   */
  resolveAllForEntity(
    entity: string,
    entityId: string,
    resolution: 'local' | 'server' | 'merge'
  ): Record<string, any> {
    const conflicts = this.getConflictsForEntity(entity, entityId);
    const resolved: Record<string, any> = {};

    for (const conflict of conflicts) {
      resolved[conflict.field] = this.resolveConflict(conflict.id, resolution);
    }

    return resolved;
  }

  /**
   * Clear all conflicts
   */
  clearConflicts(): void {
    this.conflicts = [];
  }

  /**
   * Get conflict count
   */
  getConflictCount(): number {
    return this.conflicts.length;
  }

  /**
   * Check if entity has conflicts
   */
  hasConflicts(entity?: string, entityId?: string): boolean {
    if (!entity) {
      return this.conflicts.length > 0;
    }
    if (!entityId) {
      return this.conflicts.some(c => c.entity === entity);
    }
    return this.conflicts.some(c => c.entity === entity && c.entityId === entityId);
  }

  /**
   * Generate conflict report
   */
  generateReport(): {
    totalConflicts: number;
    byEntity: Record<string, number>;
    byField: Record<string, number>;
    oldestConflict: Conflict | null;
  } {
    const byEntity: Record<string, number> = {};
    const byField: Record<string, number> = {};
    let oldestConflict: Conflict | null = null;

    for (const conflict of this.conflicts) {
      // Count by entity
      byEntity[conflict.entity] = (byEntity[conflict.entity] || 0) + 1;
      
      // Count by field
      byField[conflict.field] = (byField[conflict.field] || 0) + 1;
      
      // Track oldest
      if (!oldestConflict || conflict.localTimestamp < oldestConflict.localTimestamp) {
        oldestConflict = conflict;
      }
    }

    return {
      totalConflicts: this.conflicts.length,
      byEntity,
      byField,
      oldestConflict,
    };
  }
}

// Singleton instance
export const conflictResolver = new ConflictResolver();
